// "Follow this up in a week" — the quick way to set a due date, beside the box that
// spells it out.
//
// WHY IT EXISTS. Scheduling a follow-up is thought of as a distance ("get back to them
// in three days"), never as a date, and until now the form asked only for the date: you
// worked out 21/09 in your head and typed it. That arithmetic is the one part of this a
// computer should be doing, and getting it wrong is invisible afterwards — a date that
// is merely a few days off looks exactly like a date that was meant.
//
// IT IS NOT A SECOND WAY TO STORE A DEADLINE. There is no "in 7 days" anywhere in the
// data: these buttons compute a date and write it into the same `due_date` the text box
// edits. One value, one column, so the two controls cannot disagree and the saved task is
// identical however it was set. Which is also why the chosen button lights up by
// COMPARING against the current value rather than remembering the click — retype the
// date by hand and the matching button highlights itself; type a different one and
// nothing is highlighted, because no offset is what you meant.
import he from '../i18n/he';
import { todayStr, addDays } from '../utils/dates';
import Button from './Button';

// Tomorrow, then the intervals a follow-up is actually described in. Deliberately few:
// a strip of every number up to 30 is a calendar with extra steps.
const OFFSETS = [1, 3, 7, 14, 30];

const labelFor = (days) => (days === 1 ? he.tasks.quickTomorrow : he.tasks.quickInDays(days));

export default function DueOffsets({ value, onPick }) {
  // Counted from the browser's today, which is the right clock for ENTRY: "in 3 days"
  // means three days from the day the person sitting here is having. Whether the
  // resulting date has ARRIVED is a different question with a different answer, decided
  // once on the server (calculations/dueTasks) — this never judges a date, it only
  // produces one.
  const from = todayStr();

  return (
    <div className="field due-offsets">
      <label>{he.tasks.schedule}</label>
      <div className="seg">
        {OFFSETS.map((days) => {
          const date = addDays(from, days);
          return (
            <Offset key={days} active={value === date} label={labelFor(days)} onClick={() => onPick(date)} />
          );
        })}
        {/* A deadline is optional — a task with no date is normal — so clearing it is one
            of the choices here rather than something you have to know to do with the
            keyboard. It is `''`, the same value emptying the box produces. */}
        <Offset active={!value} label={he.tasks.quickNone} onClick={() => onPick('')} />
      </div>
    </div>
  );
}

// The shared Button in the selected/unselected pairing the filter bars already use, so
// a chosen offset looks like a chosen urgency or stage rather than like a new control.
// `type="button"` is explicit: this sits inside a dialog whose primary action is Save,
// and a submit here would schedule and save in one click.
function Offset({ active, label, onClick }) {
  return (
    <Button small type="button" variant={active ? '' : 'subtle'} onClick={onClick}>
      {label}
    </Button>
  );
}
